//# TODO: 2507500 - change file name to be generic and remove the "temporal" part
// Constants
import { culturesMap, LanguageSaturday, LanguageSunday, LocaleRegex, RegionSaturday, RegionSunday } from "@ms/uno-constants/lib/local/CultureConstants";
import { DayOfWeek, DueDateCategory, RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
// Errors
import { ArgumentError } from "@ms/uno-errors/lib/local/errors";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { format } from "@fluentui/utilities";
import { Temporal } from "temporal-polyfill-lite";
// Guaranteed-real polyfill import (bypasses the native-or-polyfill webpack alias above) used as a
// defensive fallback in getTodayPlainDate() below — see comment there for why this exists.
import { Temporal as TemporalPolyfill } from "./TemporalPolyfillImport";
// Constants for date parsing and formatting - chosen for safety and consistency
const PLACEHOLDER_DATE = {
    YEAR: 2000,
    MONTH: 1,
    DAY: 2
};
// Gregorian calendar constants
const MONTHS_IN_YEAR = 12;
// Global locale override for date utilities
let _globalLocale;
// Cache for mapping locale to its weekday offset
const _localeWeekdayOffsetCache = new Map();
/**
 * Sets the global locale to be used by all date utility functions.
 * Call this once during application initialization.
 * @param locale The locale string (e.g. "zh-CN", "fr-FR") or undefined to reset.
 */ export function setDateUtilitiesLocale(locale) {
    _globalLocale = locale;
}
/**
 * Returns the system timezone from Intl.DateTimeFormat.
 * Callers should cache this value when processing multiple dates in a batch
 * to avoid repeated Intl.DateTimeFormat instantiation overhead.
 * @returns The IANA timezone string (e.g., "America/New_York", "Europe/London")
 */ export function getSystemTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/**
 * Returns today's date as a PlainDate for the given timezone.
 * Callers should cache this value when processing multiple dates in a batch
 * to avoid repeated Temporal.Now and startOfDay() computation overhead.
 *
 * Defensive fallback: some native Temporal implementations have been observed to throw
 * for specific (valid) IANA timeZone inputs even though startup feature-detection passed
 * (root cause of a Planner blank-screen ICM). To avoid crashing the caller, this retries
 * against the bundled polyfill implementation, and — as an absolute last resort, in case
 * even the polyfill rejects the given timeZone — falls back to "UTC" so this call can
 * never throw.
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 * @param traceLogger Optional trace logger. When provided, a Warning trace is emitted each time
 * the native Temporal call throws, so the frequency of this failure mode is observable in production
 * rather than silently swallowed.
 * @returns PlainDate representing today in the specified timezone
 */ export function getTodayPlainDate(timeZone = getSystemTimeZone(), traceLogger) {
    try {
        return Temporal.Now.plainDateISO(timeZone);
    } catch (error) {
        traceLogger?.logTrace(0x1d7d9246 /* tag_35zjg */ , TraceLevel.Warning, `Native Temporal.Now.plainDateISO threw, falling back to polyfill [TimeZone=${timeZone}][Error=${error instanceof Error ? error.message : String(error)}]`);
        try {
            // Normalize back to the primary Temporal.PlainDate: in builds where the primary
            // import resolves to native Temporal, callers must not receive a differently-typed
            // TemporalPolyfill.PlainDate instance on this fallback path.
            const polyfillDate = TemporalPolyfill.Now.plainDateISO(timeZone);
            return Temporal.PlainDate.from(polyfillDate.toString());
        } catch  {
            const polyfillUtcDate = TemporalPolyfill.Now.plainDateISO("UTC");
            return Temporal.PlainDate.from(polyfillUtcDate.toString());
        }
    }
}
/**
 * Returns the effective locale for date formatting.
 * Prioritizes the global override set via {@link setDateUtilitiesLocale}
 * (typically the app's UI language), then falls back to the system/browser
 * default. Prefer this over `Intl.DateTimeFormat().resolvedOptions().locale`
 * directly so dates respect the app's UI language rather than the OS regional
 * format (which can differ — e.g. UI = `ja` but regional = `en-GB`).
 */ export function getCurrentLocale() {
    return _globalLocale || Intl.DateTimeFormat().resolvedOptions().locale;
}
// --------------------------------------------------------------------------
// Locale-aware short / long date formatters (ISO string in, locale string out)
// --------------------------------------------------------------------------
//
// Used by hot render paths (e.g. canvas grid date cells, aria labels, editor
// inputs) where we need a single source of truth so canvas paint, editor
// input, and editor's typed-value parser all agree on the user's locale.
// Without this, a user editing "20/03/2027" in en-GB sees the editor strip
// the leading zero to "20/3/2027", or an en-US user sees "3/15/2025" on the
// canvas but is forced to type "15/3/2025" — both jarring locale glitches.
//
// Performance: the canvas grid renderer's `formatShortDate` is called once
// per visible date cell on every paint pass — typically ~150 cells (30 rows
// × 5 date columns). On scroll the canvas repaints up to 60×/second.
//   Measured on Node v24 / M-class CPU:
//     `new Intl.DateTimeFormat(locale, opts).format(date)`  ≈ 35 µs/call
//     `cachedFormatter.format(date)`                        ≈  0.5 µs/call
//   ~70× faster, saves ~4 ms per paint pass — drops a chronic 60fps budget
//   hit to ~0.1 ms. Locale rarely changes after app boot, so two single-slot
//   caches keyed by locale are enough — no LRU bookkeeping required.
let _cachedShortFormatter = null;
let _cachedLongFormatter = null;
let _cachedMediumFormatter = null;
let _cachedWeekdayFormatter = null;
let _cachedPartOrder = null;
let _cachedDateFormatPattern = null;
function getShortDateFormatter() {
    const locale = getCurrentLocale();
    if (!_cachedShortFormatter || _cachedShortFormatter.locale !== locale) {
        _cachedShortFormatter = {
            locale,
            formatter: new Intl.DateTimeFormat(locale, {
                dateStyle: "short"
            })
        };
    }
    return _cachedShortFormatter.formatter;
}
function getLongDateFormatter() {
    const locale = getCurrentLocale();
    if (!_cachedLongFormatter || _cachedLongFormatter.locale !== locale) {
        _cachedLongFormatter = {
            locale,
            formatter: new Intl.DateTimeFormat(locale, {
                dateStyle: "long"
            })
        };
    }
    return _cachedLongFormatter.formatter;
}
function getMediumDateFormatter() {
    const locale = getCurrentLocale();
    if (!_cachedMediumFormatter || _cachedMediumFormatter.locale !== locale) {
        _cachedMediumFormatter = {
            locale,
            formatter: new Intl.DateTimeFormat(locale, {
                day: "numeric",
                month: "short",
                year: "numeric"
            })
        };
    }
    return _cachedMediumFormatter.formatter;
}
function getWeekdayDateFormatter() {
    const locale = getCurrentLocale();
    if (!_cachedWeekdayFormatter || _cachedWeekdayFormatter.locale !== locale) {
        _cachedWeekdayFormatter = {
            locale,
            formatter: new Intl.DateTimeFormat(locale, {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric"
            })
        };
    }
    return _cachedWeekdayFormatter.formatter;
}
/**
 * Returns the order in which the current locale prints `year`, `month`, and
 * `day` parts in `dateStyle: "short"`. Used by parsers to interpret a typed
 * `1/2/3`-style string in the same order the locale displays.
 *
 * Examples:
 *   en-US → ["month", "day", "year"]
 *   en-GB → ["day", "month", "year"]
 *   ja-JP → ["year", "month", "day"]
 */ export function getLocaleDatePartOrder() {
    const locale = getCurrentLocale();
    if (_cachedPartOrder && _cachedPartOrder.locale === locale) {
        return _cachedPartOrder.order;
    }
    // Use a sample date with distinguishable values so we can read the order
    const sample = new Date(2025, 1, 3, 12); // 2025-02-03 12:00 local
    const parts = getShortDateFormatter().formatToParts(sample);
    const order = parts.filter((p)=>p.type === "year" || p.type === "month" || p.type === "day").map((p)=>p.type);
    _cachedPartOrder = {
        locale,
        order
    };
    return order;
}
/**
 * Returns the current locale's short-date pattern as letter tokens (e.g. "MM/DD/YYYY" for en-US,
 * "DD/MM/YYYY" for en-GB, "YYYY/MM/DD" for ja-JP), for use as format guidance in date inputs and
 * accessible labels (e.g. an empty date cell's aria-label or placeholder text). Separators come
 * from the locale's own short-date formatting, so they match what {@link formatShortDate} renders.
 */ export function getLocaleDateFormatPattern() {
    const locale = getCurrentLocale();
    if (_cachedDateFormatPattern && _cachedDateFormatPattern.locale === locale) {
        return _cachedDateFormatPattern.pattern;
    }
    // Use the same sample date as getLocaleDatePartOrder so parts/separators are read consistently.
    const sample = new Date(2025, 1, 3, 12); // 2025-02-03 12:00 local
    const parts = getShortDateFormatter().formatToParts(sample);
    const pattern = parts.map((part)=>{
        switch(part.type){
            case "year":
                return "YYYY";
            case "month":
                return "MM";
            case "day":
                return "DD";
            default:
                return part.value;
        }
    }).join("");
    _cachedDateFormatPattern = {
        locale,
        pattern
    };
    return pattern;
}
/** Convert an ISO PlainDate string to a JS `Date` for `Intl.DateTimeFormat.format`.
 *  Uses local-time noon to avoid timezone-rollover changing the calendar day. */ function plainDateToJSDate(plainDate) {
    return new Date(plainDate.year, plainDate.month - 1, plainDate.day, 12);
}
/**
 * Format an ISO date string using the current locale's short style.
 * en-US → "3/15/25", ja-JP → "2025/03/15", en-GB → "15/03/2025".
 * Returns the input unchanged if it is not a valid ISO date.
 */ export function formatShortDate(dateStr) {
    try {
        const plainDate = Temporal.PlainDate.from(dateStr);
        return getShortDateFormatter().format(plainDateToJSDate(plainDate));
    } catch  {
        return dateStr;
    }
}
/**
 * Format an ISO date string using the current locale's long style (suitable
 * for screen-reader aria labels).
 * en-US → "March 15, 2025", ja-JP → "2025年3月15日".
 * Returns the input unchanged if it is not a valid ISO date.
 */ export function formatLongDate(dateStr) {
    try {
        const plainDate = Temporal.PlainDate.from(dateStr);
        return getLongDateFormatter().format(plainDateToJSDate(plainDate));
    } catch  {
        return dateStr;
    }
}
/**
 * Format an ISO date string with short weekday and medium date style.
 * en-US → "Wed, May 13, 2026", de-DE → "Mi., 13. Mai 2026".
 * Suitable for tooltips where weekday context is helpful.
 * Returns the input unchanged if it is not a valid ISO date.
 */ export function formatDateWithWeekday(dateStr) {
    try {
        const plainDate = Temporal.PlainDate.from(dateStr);
        return getWeekdayDateFormatter().format(plainDateToJSDate(plainDate));
    } catch  {
        return dateStr;
    }
}
/**
 * Format an ISO date string with medium date style (abbreviated month, no weekday).
 * en-US → "May 13, 2026", de-DE → "13. Mai 2026".
 * Suitable for compact labels/tooltips where the weekday is unnecessary.
 * Returns the input unchanged if it is not a valid ISO date.
 */ export function formatMediumDate(dateStr) {
    try {
        const plainDate = Temporal.PlainDate.from(dateStr);
        return getMediumDateFormatter().format(plainDateToJSDate(plainDate));
    } catch  {
        return dateStr;
    }
}
/**
 * Convert a JS Date (e.g. from a calendar popover's onSelect) to an ISO
 * date string (`YYYY-MM-DD`) using the date's local-time year/month/day.
 */ export function jsDateToIsoString(date) {
    return Temporal.PlainDate.from({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
    }).toString();
}
/**
 * Parse a `YYYY-MM-DD` ISO date string into a local-midnight JS Date,
 * avoiding the UTC shift that `new Date("2026-07-06")` causes.
 *
 * Validates that the parts form a real calendar date (rejects Feb 31, etc.).
 * Returns `null` for malformed or out-of-range input.
 */ export function parseISODateLocal(value) {
    const parts = value.split("-");
    if (parts.length !== 3) return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    // Guard against Date constructor normalization (e.g. 2026-02-31 → 2026-03-03)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
}
/**
 * Try to parse a user-typed string into an ISO date string using Temporal.
 *
 * Strategy:
 *  1. If the input looks like an ISO date (YYYY-MM-DD), try Temporal directly.
 *     This guards against the locale-ordered slash branch greedily mis-parsing
 *     ISO input: in en-US that branch would interpret `2026-03-15` as
 *     month=2026/day=3/year=15, which silently maps onto an unrelated date.
 *  2. Try the locale's display order on slash/dot/dash-separated input
 *     (so a user reading e.g. "15/03/2027" in en-GB and re-typing
 *     "15/03/2027" round-trips).
 *  3. Fall back to any other format Temporal natively accepts.
 *
 * Uses Temporal's `overflow: "reject"` so out-of-range values like
 * `13/40/2026` fail rather than being silently clamped to a valid date.
 *
 * 2-digit year is expanded to 2000-2099 (matches `dateStyle: "short"`'s
 * en-US output convention — `3/15/26` round-trips to 2026-03-15). Distinct
 * from {@link parseTemporalFromString}'s 1950s split rule.
 *
 * Returns null if no parse succeeds.
 */ export function parseInputToIsoString(input) {
    const trimmed = input.trim();
    if (!trimmed) return null;
    // 1. ISO `YYYY-MM-DD` shape — must be tried first or step 2 swallows it.
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) {
        try {
            return Temporal.PlainDate.from(trimmed, {
                overflow: "reject"
            }).toString();
        } catch  {
        // Fall through — invalid calendar date (e.g. 2026-13-40) → null below.
        }
    }
    // 2. Locale-ordered slash/dot/dash format
    const parts = trimmed.split(/[/\-.]/);
    if (parts.length === 3 && parts.every((s)=>/^\d+$/.test(s))) {
        const nums = parts.map((s)=>Number.parseInt(s, 10));
        if (nums.every((n)=>Number.isFinite(n) && n > 0)) {
            const order = getLocaleDatePartOrder();
            const named = {};
            order.forEach((type, i)=>{
                named[type] = nums[i];
            });
            // Two-digit year: assume 2000-2099 to match Intl.DateTimeFormat's "short" output
            // (en-US `dateStyle: "short"` emits a 2-digit year by default — `3/15/26`).
            if (named.year != null && named.year < 100) named.year += 2000;
            if (named.year != null && named.month != null && named.day != null) {
                try {
                    return Temporal.PlainDate.from({
                        year: named.year,
                        month: named.month,
                        day: named.day
                    }, {
                        overflow: "reject"
                    }).toString();
                } catch  {
                // Fall through to generic parse
                }
            }
        }
    }
    // 3. Any other format Temporal natively parses.
    try {
        return Temporal.PlainDate.from(trimmed, {
            overflow: "reject"
        }).toString();
    } catch  {
        return null;
    }
}
/**
 * Get the default due date for a due date category using Temporal
 * @param dueDateCategory The due date category
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export function getDefaultDueDate(dueDateCategory, timeZone = getSystemTimeZone()) {
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    switch(dueDateCategory){
        case DueDateCategory.Late:
            // Yesterday
            return toUTC10am(now.subtract({
                days: 1
            }));
        case DueDateCategory.Today:
            // Current day
            return toUTC10am(now);
        case DueDateCategory.Tomorrow:
            // End of current day + 1 day
            return toUTC10am(now.add({
                days: 1
            }));
        case DueDateCategory.ThisWeek:
            // Last day of range calculated for this week
            return toUTC10am(now.add({
                days: 7 - now.dayOfWeek
            }));
        case DueDateCategory.NextWeek:
            // Last day of range calculated for next week
            return toUTC10am(now.add({
                days: 14 - now.dayOfWeek
            }));
        case DueDateCategory.Future:
            // First day of range calculated for future
            return toUTC10am(now.add({
                days: 15 - now.dayOfWeek
            }));
        case DueDateCategory.NoDate:
        default:
            return null;
    }
}
/**
 * Random date generator using Temporal
 * @param start Start of range for date in milliseconds
 * @param end End of range for date in milliseconds
 */ export function randomDate(start, end) {
    if (end < start) {
        throw new ArgumentError("start", "start must be < end");
    }
    const randomTimestamp = Math.floor(start + Math.random() * (end - start));
    // Convert timestamp to Temporal
    return Temporal.Instant.fromEpochMilliseconds(randomTimestamp).toZonedDateTimeISO("UTC");
}
/**
 * Generate a random Temporal.PlainDateTime prior to the specified timestamp
 * @param timestamp The timestamp to generate a random date before
 */ export function generateRandomDateBefore(timestamp) {
    // Convert to milliseconds for consistent calculation
    const endTime = timestamp.toInstant().epochMilliseconds;
    const startTime = endTime - 10000000000;
    // Generate random date using our existing randomDate function
    return randomDate(startTime, endTime);
}
/**
 * Check if two Temporal.PlainDateTime are on the same day (ignore time)
 * @param date1 First date
 * @param date2 Second date
 */ export function isSameDay(date1, date2) {
    if (date1 == null && date2 == null) {
        return true;
    }
    if (date1 == null || date2 == null) {
        return false;
    }
    // Convert both dates to UTC before comparing to handle timezone differences
    const date1UTC = date1.toInstant().toZonedDateTimeISO("UTC");
    const date2UTC = date2.toInstant().toZonedDateTimeISO("UTC");
    return date1UTC.year === date2UTC.year && date1UTC.month === date2UTC.month && date1UTC.day === date2UTC.day;
}
/**
 * Method to convert Temporal.ZonedDateTime to UTC at 10AM
 * @param originalDate ZonedDateTime object to convert to UTC 10AM
 */ export function toUTC10am(originalDate) {
    // Create a new UTC date with the same year/month/day but at 10AM
    return Temporal.ZonedDateTime.from({
        timeZone: "UTC",
        year: originalDate.year,
        month: originalDate.month,
        day: originalDate.day,
        hour: 10,
        minute: 0,
        second: 0,
        millisecond: 0
    });
}
/**
 * Method to convert Temporal.PlainDateTime to UTC at 10AM
 * @param date Temporal.PlainDateTime object to convert to UTC 10AM.
 */ export function normalizeDate(originalDate) {
    if (originalDate != null) {
        return toUTC10am(originalDate);
    }
    return null;
}
/**
 * Convert a Temporal.ZonedDateTime to a JavaScript Date object with only date components
 * Used for Calendar components that expect Date objects for date-only scenarios
 * @param date The Temporal.ZonedDateTime to convert
 * @returns JavaScript Date object with same year, month, and day, or undefined if input is null
 */ export function createDateFromTemporal(date) {
    if (!date) {
        return undefined;
    }
    return new Date(date.year, date.month - 1, date.day);
}
/**
 * Convert a Temporal.PlainDate to a JavaScript Date object with only date components
 * Used for Calendar components that expect Date objects for date-only scenarios
 * @param plainDate The Temporal.PlainDate to convert
 * @returns JavaScript Date object with same year, month, and day, or null if input is null
 */ export function createDateFromPlainDate(plainDate) {
    if (!plainDate) {
        return null;
    }
    return new Date(plainDate.year, plainDate.month - 1, plainDate.day);
}
/**
 * Convert a JavaScript Date object to a Temporal.PlainDate with only date components
 * Note: getMonth() is 0-indexed so +1 is needed for Temporal's 1-indexed months
 * @param date The JavaScript Date to convert
 * @returns Temporal.PlainDate with same year, month, and day, or null if input is null
 */ export function createPlainDateFromDate(date) {
    if (!date) {
        return null;
    }
    return Temporal.PlainDate.from({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
    });
}
/**
 * Convert a JavaScript Date object to Temporal.ZonedDateTime with time set to midnight
 * @param localDate The local Date object
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 * @returns Temporal.ZonedDateTime with same year, month, day and time set to midnight in specified timezone, or null if input is null
 */ export function createTemporalFromDate(localDate, timeZone = getSystemTimeZone()) {
    if (localDate == null) {
        return null;
    }
    return Temporal.ZonedDateTime.from({
        year: localDate.getFullYear(),
        month: localDate.getMonth() + 1,
        day: localDate.getDate(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        timeZone
    });
}
/**
 * Method to format a Temporal.PlainDateTime into locale specific string
 * MMM DD if within a year of today or long form if over a year away
 * @param date Temporal.PlainDateTime to format
 * @param [useYear] Use the year even if the date is within a year of today
 * @param [formatToken] Options to format the date
 * @param [locale] Locale to use for formatting
 * @returns Date formatted using the provided options
 */ export function formatDate(date, useYear, formatToken = "L", locale) {
    const now = Temporal.Now.zonedDateTimeISO();
    const target = date instanceof Temporal.Instant ? date.toZonedDateTimeISO(now.timeZoneId) : date;
    const sameYear = now.year === target.year;
    const omitYear = !useYear && sameYear;
    return formatLikeMoment(target, formatToken, locale, {
        omitYear
    });
}
/**
 * Format a Date into a locale-specific string (short form, no year)
 * @param date Date to format
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export function formatJSDate(date, timeZone = getSystemTimeZone()) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zoned = instant.toZonedDateTimeISO(timeZone);
    return formatDate(zoned, false); // useYear = false
}
/**
 * Format a Date using a custom format token like \"LL\", \"lll\", etc.
 * * @param date Date to format
 */ export function formatJSDateCustom(date, format) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    return formatLikeMoment(instant, format);
}
/**
 * Format a Date into long locale-specific string including the year
 * @param date Date to format
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export function formatJSDateLongForm(date, timeZone = getSystemTimeZone()) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zoned = instant.toZonedDateTimeISO(timeZone);
    return formatDate(zoned, true);
}
/**
 * Parse the date from the string using Temporal to ensure using the correct locale
 * @param dateString String to parse date
 * @param strict Whether to use strict parsing or not. Default is true
 */ export function parseDateFromString(dateString, strict = true) {
    if (!dateString?.trim()) {
        return null;
    }
    const plainDate = parseTemporalFromString(dateString, strict);
    if (plainDate) {
        return new Date(plainDate.year, plainDate.month - 1, plainDate.day); // Convert to JS Date
    }
    return null;
}
/**
 * Gets the locale-specific ordering of date components (year, month, day) as
 * a numeric tuple. Internal helper for {@link parseTemporalFromString}'s regex
 * pattern; for new callers prefer the named {@link getLocaleDatePartOrder}.
 * @returns Tuple of [yearIndex, monthIndex, dayIndex] where each is 0, 1, or 2
 */ function getLocaleDateOrdering() {
    const order = getLocaleDatePartOrder();
    return [
        order.indexOf("year"),
        order.indexOf("month"),
        order.indexOf("day")
    ];
}
/**
 * Parse the date from a string into a new Temporal object in the correct locale
 * Supports multiple formats:
 * - Full dates: "02/12/2025", "2025/02/12" (with 4-digit year)
 * - Partial dates: "02/12", "2/5" (assumes current year)
 * - 2-digit year: "02/12/26", "70/02/25" (00-50 = 2000-2050, 51-99 = 1951-1999)
 *
 * @param dateString String to parse date
 * @param strict Whether to use strict parsing or not. Default is true
 */ export function parseTemporalFromString(dateString, strict = true) {
    if (!dateString?.trim()) {
        return null;
    }
    const trimmed = dateString.trim();
    // Get locale-specific date component ordering once
    const [yearIndex, monthIndex, dayIndex] = getLocaleDateOrdering();
    // Try full (year-included) locale-aware date first
    const fullRegex = localeDatePattern();
    const fullMatch = fullRegex.exec(trimmed);
    if (fullMatch?.groups) {
        const { year, month, day } = fullMatch.groups;
        const y = Number(year);
        const m = Number(month);
        const d = Number(day);
        try {
            return Temporal.PlainDate.from({
                year: y,
                month: m,
                day: d
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    // ---------------------------------------------------------------------
    // Partial date support (no year) e.g. "3/4", "03-04", locale variant.
    // We infer current year & ordering based on locale format parts.
    // ---------------------------------------------------------------------
    // Accept two numeric components (1-2 digits) separated by common delimiters
    const partialPattern = /^\s*(?<first>\d{1,2})[\/\-.\s]+(?<second>\d{1,2})\s*$/;
    const partialMatch = partialPattern.exec(trimmed);
    if (partialMatch?.groups) {
        const firstNum = Number(partialMatch.groups.first);
        const secondNum = Number(partialMatch.groups.second);
        // Use locale ordering to determine which is month vs day
        const month = monthIndex < dayIndex ? firstNum : secondNum;
        const day = monthIndex < dayIndex ? secondNum : firstNum;
        const year = getTodayPlainDate().year;
        try {
            return Temporal.PlainDate.from({
                year,
                month,
                day
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    // ---------------------------------------------------------------------
    // Three-part date with 2-digit or 4-digit year support
    // Examples: "9/26/24" → "9/26/2024"
    // Respects locale ordering: MDY, DMY, or YMD
    // ---------------------------------------------------------------------
    const threePartPattern = /^\s*(?<first>\d{1,4})[\/\-.\s]+(?<second>\d{1,2})[\/\-.\s]+(?<third>\d{1,4})\s*$/;
    const threePartMatch = threePartPattern.exec(trimmed);
    if (threePartMatch?.groups) {
        const firstNum = Number(threePartMatch.groups.first);
        const secondNum = Number(threePartMatch.groups.second);
        const thirdNum = Number(threePartMatch.groups.third);
        // Map the three input values to year/month/day based on locale ordering
        const values = [
            firstNum,
            secondNum,
            thirdNum
        ];
        let year = values[yearIndex];
        const month = values[monthIndex];
        const day = values[dayIndex];
        // Handle 2-digit years: 00-84 = 2000-2084, 85-99 = 1985-1999
        if (year < 100) {
            year += year <= 84 ? 2000 : 1900;
        }
        try {
            return Temporal.PlainDate.from({
                year,
                month,
                day
            }, strict ? {
                overflow: "reject"
            } : {});
        } catch  {
            return null;
        }
    }
    return null;
}
/**
 * Check whether a date is today
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isToday(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    return date.equals(todayPlainDate);
}
/**
 * Check whether a date is in the past
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isPast(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    return Temporal.PlainDate.compare(date, todayPlainDate) < 0;
}
/**
 * Check whether a date is tomorrow
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isTomorrow(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    const tomorrowPlainDate = todayPlainDate.add({
        days: 1
    });
    return date.equals(tomorrowPlainDate);
}
/**
 * Calculate the number of days since the start of the week for a given date,
 * based on the locale-aware week start day.
 * @param date a Temporal.PlainDate or Temporal.ZonedDateTime to calculate from
 * @returns Number of days (0-6) since the locale's week start day
 */ function getDaysSinceWeekStart(date) {
    const offset = getLocaleWeekdayOffset();
    // dayOfWeek is 1 (Mon) – 7 (Sun). Subtracting 1 converts to 0-based from Monday.
    // Subtracting the locale offset shifts the origin to the locale's week-start day.
    // The +7 and %7 ensure a non-negative result in the 0-6 range
    // e.g.
    // Sunday in a Sunday-start locale: (7 - 1 - (-1)  + 7) % 7 = 0 days since week start
    // Monday in a Saturday-start locale: (1 - 1 - (-2) + 7) % 7 = 2 days since week start
    return (date.dayOfWeek - 1 - offset + 7) % 7;
}
/**
 * Check whether a date lies in current week
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isInCurrentWeek(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    // Calculate start and end of current week using locale-aware week start
    const startOfWeekPlain = todayPlainDate.subtract({
        days: getDaysSinceWeekStart(todayPlainDate)
    });
    const endOfWeekPlain = startOfWeekPlain.add({
        days: 6
    });
    return Temporal.PlainDate.compare(date, startOfWeekPlain) >= 0 && Temporal.PlainDate.compare(date, endOfWeekPlain) <= 0;
}
/**
 * Check whether a date is after end of next week
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isAfterEndOfNextWeek(date, todayPlainDate) {
    if (date == null) {
        return false;
    }
    // Calculate end of next week using PlainDate
    const endOfNextWeekPlain = todayPlainDate.add({
        days: 13 - getDaysSinceWeekStart(todayPlainDate)
    });
    return Temporal.PlainDate.compare(date, endOfNextWeekPlain) > 0;
}
/**
 * Check whether a date lies in next week
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isInNextWeek(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    // Calculate start and end of next week using PlainDate
    const startOfNextWeekPlain = todayPlainDate.add({
        days: 7 - getDaysSinceWeekStart(todayPlainDate)
    });
    const endOfNextWeekPlain = startOfNextWeekPlain.add({
        days: 6
    });
    return Temporal.PlainDate.compare(date, startOfNextWeekPlain) >= 0 && Temporal.PlainDate.compare(date, endOfNextWeekPlain) <= 0;
}
/**
 * Check whether a date is in the current month in specified timezone
 * @param date ZonedDateTime to check
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export function isInCurrentMonth(date, timeZone = getSystemTimeZone()) {
    if (date == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = date.withTimeZone(timeZone);
    return now.year === localDate.year && now.month === localDate.month;
}
/**
 * Check whether a date was yesterday
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function isYesterday(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    const yesterdayPlainDate = todayPlainDate.subtract({
        days: 1
    });
    return date.equals(yesterdayPlainDate);
}
/**
 * Check whether a date was in past few days (between yesterday and 6 days ago)
 * @param date PlainDate to check (should already be in the desired timezone)
 * @param todayPlainDate Today's PlainDate for comparison
 */ export function inPastFewDays(date, todayPlainDate = getTodayPlainDate()) {
    if (date == null) {
        return false;
    }
    // Calculate boundaries using PlainDate
    const yesterdayPlain = todayPlainDate.subtract({
        days: 1
    });
    const sixDaysAgoPlain = todayPlainDate.subtract({
        days: 6
    });
    return Temporal.PlainDate.compare(date, yesterdayPlain) < 0 && Temporal.PlainDate.compare(date, sixDaysAgoPlain) > 0;
}
/**
 * Check whether date falls within the last week (7 days)
 * @param date Temporal.ZonedDateTime
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export const isWithinPastWeek = (date, timeZone = getSystemTimeZone())=>{
    if (date === null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const durationInDays = Math.abs(date.until(now).total({
        unit: "day"
    }));
    return durationInDays < 7;
};
/**
 * Check whether provided Temporal.ZonedDateTime was moments ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inLastMinute(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    return Math.abs(duration.total({
        unit: "second"
    })) < 120;
}
/**
 * Check whether date falls within the last minute (45 seconds)
 * @param date Temporal.ZonedDateTime
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export const isWithinPastMinute = (date, timeZone = getSystemTimeZone())=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const seconds = date.until(now).total({
        unit: "second"
    });
    return seconds >= 0 && seconds < 45;
};
/**
 * Check whether provided Temporal.ZonedDateTime was minutes ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastFewMinutes(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 2 && minutes <= 60;
}
/**
 * Check whether provided Temporal.ZonedDateTime was an hour ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastHour(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 61 && minutes <= 90;
}
/**
 * Check whether date falls within the last hour (60 minutes)
 * @param date ZonedDateTime to check
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export const isWithinPastHour = (date, timeZone = getSystemTimeZone())=>{
    if (date == null) return false;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const duration = date.until(now);
    const minutes = duration.total({
        unit: "minutes"
    });
    return minutes < 60;
};
/**
 * Check whether date falls within the last day (24 hours)
 * @param date Temporal.ZonedDateTime
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export const isWithinPastDay = (date, timeZone = getSystemTimeZone())=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const hours = date.until(now).total({
        unit: "hour"
    });
    return hours >= 0 && hours < 24;
};
/**
 * Check whether provided Temporal.ZonedDateTime was hours ago
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inPastFewHours(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const minutes = Math.abs(duration.total({
        unit: "minute"
    }));
    return minutes >= 91 && minutes <= 480;
}
/**
 * Check whether provided Temporal.ZonedDateTime was a with last year
 * @param timeStamp The Temporal.ZonedDateTime to compare
 */ export function inLastYear(timeStamp) {
    if (timeStamp == null) {
        return false;
    }
    const now = Temporal.Now.zonedDateTimeISO();
    const duration = timeStamp.until(now);
    const years = Math.abs(duration.total({
        unit: "year",
        relativeTo: timeStamp
    }));
    return years < 1;
}
/**
 * Check whether date falls within the last year (365 days)
 * @param date Temporal.ZonedDateTime
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export const isWithinPastYear = (date, timeZone = getSystemTimeZone())=>{
    if (!date) return false;
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const days = date.until(now).total({
        unit: "day"
    });
    return days >= 0 && days < 365;
};
/**
 * Compare a timestamp to the current time and return a string indicating about how much time has passed.
 * @param timestamp the temporal timestamp to compare
 * @param Strings Localized strings object
 * @param useUppercase When true, returns "Just now" instead of "just now" for standalone display (e.g. column cells).
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 * @param useShortDate When true, the 1-6 day range renders as a bare date ("Mar 12") instead of
 * "{weekday} at {time}" ("Mon at 9:00 PM"). For narrow surfaces such as task history cards, where the
 * longer form doesn't fit. All other ranges are unaffected.
 */ export const getElapsedTimeApproximation = (timestamp, Strings, useUppercase = false, timeZone = getSystemTimeZone(), useShortDate = false)=>{
    if (timestamp == null) {
        return "";
    }
    const zdt = timestamp instanceof Temporal.ZonedDateTime ? timestamp : timestamp.toZonedDateTimeISO(timeZone);
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const duration = now.since(zdt, {
        largestUnit: "days"
    });
    if (isWithinPastMinute(zdt, timeZone)) {
        // if plan was accessed within the past 45 seconds, return "just now" or "Just now" (uppercase)
        return useUppercase ? Strings.LastAccessed_LastAccessedJustNowUppercase : Strings.LastAccessed_LastAccessedJustNow;
    }
    if (isWithinPastHour(zdt, timeZone)) {
        // if plan was accessed within the past hour, return "{Minutes}m ago" (minimum: "1m ago")
        const durationInMinutes = duration.minutes <= 2 ? 1 : duration.minutes;
        return format(Strings.LastAccessed_LastAccessedMinutesAgo, durationInMinutes);
    }
    if (isWithinPastDay(zdt, timeZone)) {
        // if plan was accessed within the past day, return "{Hours}h ago"
        return format(Strings.LastAccessed_LastAccessedHoursAgo, duration.hours);
    }
    if (isWithinPastWeek(zdt, timeZone)) {
        if (useShortDate) {
            // Narrow surfaces drop the weekday and time; the caller is expected to surface the full
            // timestamp elsewhere (e.g. a hover tooltip) so no information is lost.
            return formatDate(zdt, false, "ll");
        }
        // if plan was accessed within the past week, return "{DayShort} at {Time AM/PM}"
        return format(Strings.LastAccessed_LastAccessedDayAndTime, formatLikeMoment(zdt, "ddd"), formatLikeMoment(zdt, "LT"));
    }
    const useYear = !isWithinPastYear(zdt, timeZone);
    return formatDate(zdt, useYear, "ll");
};
/**
 * Map a Temporal.PlainDateTime day to a DayOfWeek enum based on the day being in a range from 0-6.
 * @param temporalDate Temporal.PlainDateTime day
 */ export function getDayOfWeekFromIndex(temporalDate) {
    switch(temporalDate.dayOfWeek){
        case 1:
            return DayOfWeek.Monday;
        case 2:
            return DayOfWeek.Tuesday;
        case 3:
            return DayOfWeek.Wednesday;
        case 4:
            return DayOfWeek.Thursday;
        case 5:
            return DayOfWeek.Friday;
        case 6:
            return DayOfWeek.Saturday;
        case 7:
        default:
            return DayOfWeek.Sunday;
    }
}
/**
 * Map a DayOfWeek enum to a Temporal.PlainDateTime day index.
 * @param dayOfWeek DayOfWeek enum
 */ export function getDayIndexFromDayOfWeek(dayOfWeek) {
    switch(dayOfWeek){
        case DayOfWeek.Monday:
            return 1;
        case DayOfWeek.Tuesday:
            return 2;
        case DayOfWeek.Wednesday:
            return 3;
        case DayOfWeek.Thursday:
            return 4;
        case DayOfWeek.Friday:
            return 5;
        case DayOfWeek.Saturday:
            return 6;
        case DayOfWeek.Sunday:
        default:
            return 7;
    }
}
/**
 * Get the dayOfWeek array for a week, starting on a specific start weekday.
 * @param startDayIndex The zero-based index of the start day of the week. Should be within 0-6.
 */ export function getDaysOfWeekByStartDay(startDayIndex) {
    const daysOfWeek = [
        DayOfWeek.Sunday,
        DayOfWeek.Monday,
        DayOfWeek.Tuesday,
        DayOfWeek.Wednesday,
        DayOfWeek.Thursday,
        DayOfWeek.Friday,
        DayOfWeek.Saturday
    ];
    if (startDayIndex < 0 || startDayIndex > 6) {
        return daysOfWeek;
    }
    const splicedDaysOfWeek = daysOfWeek.splice(startDayIndex);
    return splicedDaysOfWeek.concat(daysOfWeek);
}
/**
 * Map a Temporal.PlainDate day to a RelativeWeekIndex enum with locale support.
 * @param date Temporal.PlainDate date
 */ export function getWeekIndex(date) {
    const firstDayOfMonth = date.with({
        day: 1
    });
    const weekInMonth = Math.ceil((date.day + firstDayOfMonth.dayOfWeek - 1) / 7);
    const totalWeeks = Math.ceil((date.daysInMonth + firstDayOfMonth.dayOfWeek - 1) / 7);
    if (weekInMonth === 1) return RelativeWeekIndex.First;
    if (weekInMonth === 2) return RelativeWeekIndex.Second;
    if (weekInMonth === 3) return RelativeWeekIndex.Third;
    if (weekInMonth === 4 && weekInMonth !== totalWeeks) return RelativeWeekIndex.Fourth;
    return RelativeWeekIndex.Last;
}
/**
 * Map string month to a month index, to handle non-latin based languages for moment not able to generate the month index in en.
 * This function returns 1 based month index.
 * @param month The month string in en
 */ export function getMonthIndexFromMonth(month) {
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];
    return months.indexOf(month) + 1;
}
export var TimeRange = /*#__PURE__*/ function(TimeRange) {
    // Last 7 days
    TimeRange["LastWeek"] = "lastWeek";
    // Last 14 days
    TimeRange["LastTwoWeeks"] = "lastTwoWeeks";
    // Last 30 days
    TimeRange["LastMonth"] = "lastMonth";
    // Last 90 days
    TimeRange["LastQuarter"] = "lastQuarter";
    return TimeRange;
}({});
export const timeRangeToDays = (reportingPeriod)=>{
    switch(reportingPeriod){
        case "lastWeek":
            return 7;
        case "lastTwoWeeks":
            return 14;
        case "lastMonth":
            return 30;
        case "lastQuarter":
            return 90;
        default:
            return 0;
    }
};
/**
 * Calculate the difference between a timestamp and now.
 * @param timeStamp - The ZonedDateTime to compare with now
 * @param unit - The unit to return the difference in:
 *   - "second" - Returns difference in seconds
 *   - "minute" - Returns difference in minutes
 *   - "hour" - Returns difference in hours
 *   - "day" - Returns difference in days
 *   - "millisecond" (default) - Returns difference in milliseconds
 * @param timeZone - Optional timezone string. If not provided, uses system timezone.
 */ export function calculateFromNow(timeStamp, unit = "millisecond", timeZone = getSystemTimeZone()) {
    if (timeStamp == null) {
        return 0;
    }
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const localDate = timeStamp.withTimeZone(timeZone);
    const duration = now.until(localDate);
    return duration.total({
        unit
    });
}
/**
 * Generates a locale-aware date format pattern, dynamically building a strict regex.
 * This is equivalent to moment.localeData().longDateFormat("L") which:
 * - For 'en-US' returns "MM/DD/YYYY"
 * - For 'fr' returns "DD/MM/YYYY"
 * - For other locales returns their appropriate short date format
 * @returns A RegExp that matches the current locale's short date format.
 */ function localeDatePattern() {
    // Refresh locale in case system setting changed, similar to moment.locale()
    const locale = getCurrentLocale();
    // Create formatter for numeric dates with 2-digit month/day and 4-digit year
    // This matches moment's "L" format which always uses numbers (not month names)
    const formatter = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });
    // Use a sample date to get the format parts
    // moment internally does the same to determine format order
    const sampleDate = new Date(2001, 10, 22); // Example date (Nov 22, 2001)
    const parts = formatter.formatToParts(sampleDate);
    // Build regex pattern that matches moment's "L" format:
    // - Allows whitespace like moment
    // - Captures named groups for year/month/day
    // - Preserves locale-specific separators
    let pattern = "^\\s*"; // Allow leading whitespace like moment
    for (const part of parts){
        if (part.type === "day") {
            pattern += "(?<day>\\d{1,2})"; // DD - Capture 2-digit day
        } else if (part.type === "month") {
            pattern += "(?<month>\\d{1,2})"; // MM - Capture 2-digit month
        } else if (part.type === "year") {
            pattern += "(?<year>\\d{4})"; // YYYY - Capture 4-digit year
        } else if (part.type === "literal") {
            // Escape separators (/, -, ., etc) like moment does
            pattern += part.value.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
        }
    }
    pattern += "\\s*$"; // Allow trailing whitespace like moment
    // Create RegExp that will strictly match the locale's date format
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp(pattern);
}
/**
 * -------------------------------------------------------------------------
 * Temporal Locale weekday handling
 * These functions are used to calculate the start of the week based on the locale
 * -------------------------------------------------------------------------
 */ /**
 * Take the region and language and calculate the start date
 * @param region region code
 * @param language language code
 * @returns sunday, monday or saturday as DayOfWeek
 */ export function weekStart(region, language) {
    if (region) {
        if (RegionSunday.includes(region?.toLowerCase())) {
            return DayOfWeek.Sunday;
        } else if (RegionSaturday.includes(region?.toLowerCase())) {
            return DayOfWeek.Saturday;
        }
    } else if (language) {
        if (LanguageSunday.includes(language.toLowerCase())) {
            return DayOfWeek.Sunday;
        } else if (LanguageSaturday.includes(language.toLowerCase())) {
            return DayOfWeek.Saturday;
        }
    }
    return DayOfWeek.Monday;
}
/**
 * Take the date locale and calculate the start date
 * @param locale locale string
 * @returns sunday, monday or saturday as DayOfWeek
 */ export function weekStartLocale(locale) {
    // Guard against infinite recursion
    if (!locale) {
        return DayOfWeek.Monday; // Default fallback
    }
    const parts = locale.match(LocaleRegex);
    if (!parts) {
        // If no match and not "en-US", try fallback
        return locale !== "en-US" ? weekStartLocale("en-US") : DayOfWeek.Sunday;
    }
    const localeValidationArr = locale.split("-");
    // Safety check for correct locale format
    if (localeValidationArr[0] !== parts[1] || localeValidationArr.length > 1 && localeValidationArr[localeValidationArr.length - 1] !== parts[4]) {
        // If invalid format and not "en-US", try fallback
        return locale !== "en-US" ? weekStartLocale("en-US") : DayOfWeek.Sunday;
    }
    const region = parts[4];
    const language = parts[1];
    // Handle fallbacks for certain locales
    if (!region && culturesMap.fallbacks[language]) {
        const fallbackLocale = culturesMap.fallbacks[language];
        // Prevent recursive fallback
        return fallbackLocale !== locale ? weekStartLocale(fallbackLocale) : DayOfWeek.Monday;
    }
    return weekStart(region, language);
}
/**
 * Calculates the index of the first day of the week based on the current locale.
 * @param isIndexZero boolean (index = 1 for Temporal objects, 0 for Fluent Calendar controls)
 * @returns Returns index of Sunday, Monday or Saturday as a number (0-6) OR (1-7) based on the parameter.
 */ export function weekStartIndexLocale(isIndexZero = false, locale = getCurrentLocale()) {
    const firstDayOfWeek = weekStartLocale(locale);
    if (isIndexZero) {
        return getDayIndexFromDayOfWeek(firstDayOfWeek) % 7; // Ensure it wraps around for 0-6 range
    }
    return getDayIndexFromDayOfWeek(firstDayOfWeek);
}
/**
 * Calculate weekday offset based on current locale
 * @returns offset: -1 for Sunday start, -2 for Saturday start, 0 for Monday start
 */ export function getLocaleWeekdayOffset() {
    const locale = getCurrentLocale();
    if (_localeWeekdayOffsetCache.has(locale)) {
        return _localeWeekdayOffsetCache.get(locale);
    }
    const startDay = weekStartLocale(locale);
    let offset;
    switch(startDay){
        case DayOfWeek.Sunday:
            offset = -1;
            break;
        case DayOfWeek.Saturday:
            offset = -2;
            break;
        case DayOfWeek.Monday:
        default:
            offset = 0;
            break;
    }
    _localeWeekdayOffsetCache.set(locale, offset);
    return offset;
}
/**
 * Get first day of week in current locale
 * @param date ZonedDateTime to get first day of week for
 * @returns ZonedDateTime set to first day of week in current locale
 */ export function firstDayOfWeek(date) {
    return date.startOfDay().subtract({
        days: getDaysSinceWeekStart(date)
    });
}
/**
 * Get last day of week in current locale
 * @param date ZonedDateTime to get last day of week for
 * @returns ZonedDateTime set to last day of week in current locale
 */ export function lastDayOfWeek(date) {
    return date.startOfDay().subtract({
        days: getDaysSinceWeekStart(date)
    }).add({
        days: 6
    });
}
/**
 * Get locale weekdays in long format
 * @returns Array of weekday names in current locale order
 */ export const localeWeekdaysLong = ()=>{
    const offset = getLocaleWeekdayOffset();
    const weekdays = [
        DayOfWeek.Monday,
        DayOfWeek.Tuesday,
        DayOfWeek.Wednesday,
        DayOfWeek.Thursday,
        DayOfWeek.Friday,
        DayOfWeek.Saturday,
        DayOfWeek.Sunday
    ];
    // Rotate array based on offset
    const resultWeekdays = [];
    const length = weekdays.length;
    for(let i = 0; i < length; i++){
        resultWeekdays[i] = weekdays[(i + 7 + offset) % length];
    }
    return resultWeekdays;
};
/**
 * Get locale month in certain format
 * @param temporalDate Temporal.ZonedDateTime or Temporal.Instant
 * @param format Format of the month name (long, short)
 * @returns Month name in current locale order
 */ export function getMonthName(temporalDate, format = "long") {
    const dateTime = temporalDate instanceof Temporal.Instant ? temporalDate : temporalDate.toInstant();
    return new Intl.DateTimeFormat(getCurrentLocale(), {
        month: format
    }).format(new Date(dateTime.epochMilliseconds));
}
/**
 * Get locale weekday in certain format
 * @param temporalDate Temporal.ZonedDateTime or Temporal.Instant
 * @param format Format of the weekday name (long, short)
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 * @returns Weekday name in current locale order
 */ export function getWeekdayName(date, format = "long", timeZone = getSystemTimeZone()) {
    const locale = getCurrentLocale();
    const zoned = date instanceof Temporal.Instant ? date.toZonedDateTimeISO(timeZone) : date;
    // Monday-base
    const offset = getLocaleWeekdayOffset();
    const weekdayIndex = (zoned.dayOfWeek - 1 + offset + 7) % 7;
    // Fixed Monday: 2000-01-03, then add weekdayIndex
    const utcDate = new Date(Date.UTC(2000, 0, 3 + weekdayIndex));
    // Format only the weekday in the target locale; lock to UTC to avoid TZ shifts
    return new Intl.DateTimeFormat(locale, {
        weekday: format,
        timeZone: "UTC"
    }).format(utcDate);
}
/**
 * Get all month names in the current locale
 * @param format Format of the month name (long, short)
 * @returns Array of month names in current locale order
 */ export function getAllMonthNames(format = "long") {
    const locale = getCurrentLocale();
    const dtf = new Intl.DateTimeFormat(locale, {
        month: format
    });
    return Array.from({
        length: MONTHS_IN_YEAR
    }, (_, i)=>dtf.format(new Date(PLACEHOLDER_DATE.YEAR, i, 1)));
}
/**
 * Get all weekday names starting from Sunday by locale language
 * @param format Format of the weekday name (long, short)
 * @returns Array of weekday names starting from Sunday by locale language
 */ export function getAllWeekdayNames(format = "long") {
    const locale = getCurrentLocale();
    // use utc to avoid timezone shifting the day
    // e.g. in en-US, 2000-01-02 is Sunday, but in Asia/Shanghai it's already Monday 2000-01-03
    // so we use UTC to avoid this issue
    const dtf = new Intl.DateTimeFormat(locale, {
        weekday: format,
        timeZone: "UTC"
    });
    // Base array starting on Sunday, 2000-01-02 is a Sunday
    return Array.from({
        length: 7
    }, (_, i)=>dtf.format(new Date(Date.UTC(2000, 0, 2 + i))));
}
/**
 * -------------------------------------------------------------------------
 * Temporal formatting handling
 * These functions are used to format dates in a locale/zone-aware manner
 * For replacing moment.js formatting
 * -------------------------------------------------------------------------
 */ /**
 * Format a Temporal.ZonedDateTime using moment-like tokens: "L", "LL", "LLL", "LLLL"
 * Note: We do not want to format exactly like moment, Intl is different for some format
 *
 * Examples:
 * const instant = Temporal.Instant.from("2016-10-13T12:00:00Z");
 * const zdt = instant.toZonedDateTimeISO("America/New_York");
 * formatLikeMoment(zdt, "L");     // → "10/13/16"
 * formatLikeMoment(zdt, "LL");    // → "October 13, 2016"
 * formatLikeMoment(zdt, "LLL");   // → "October 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "LLLL");  // → "Thursday, October 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "l");     // → "10/13/16"
 * formatLikeMoment(zdt, "ll");    // → "Oct 13, 2016"
 * formatLikeMoment(zdt, "lll");   // → "Oct 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "llll"); // → "Thu, Oct 13, 2016 at 8:00 AM"
 * formatLikeMoment(zdt, "dd");    // → "T"
 * formatLikeMoment(zdt, "ddd");   // → "Thu"
 * formatLikeMoment(zdt, "dddd"); // → "Thursday"
 * formatLikeMoment(zdt, "d");     // → "4"
 * formatLikeMoment(zdt, "LT");    // → "8:00 AM"
 * @param dateTime Temporal.ZonedDateTime | Temporal.Instant
 * @param formatToken IFormatToken - Format token to use for formatting
 * @param [locale] Optional locale, default "en-US"
 * @param [formatOptions] Optional options for Intl.DateTimeFormat
 * @returns Formatted string
 */ export function formatLikeMoment(dateTime, formatToken, locale, formatOptions) {
    // Get the locale from the system if not provided
    const resolvedLocale = locale || getCurrentLocale();
    const formatMap = {
        L: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        },
        LL: {
            dateStyle: "long"
        },
        LLL: {
            dateStyle: "long",
            timeStyle: "short"
        },
        LLLL: {
            dateStyle: "full",
            timeStyle: "short"
        },
        l: {
            month: "numeric",
            day: "numeric",
            year: "numeric"
        },
        ll: {
            dateStyle: "medium"
        },
        lll: {
            dateStyle: "medium",
            timeStyle: "short"
        },
        llll: {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric"
        },
        dd: {
            weekday: "narrow"
        },
        ddd: {
            weekday: "short"
        },
        dddd: {
            weekday: "long"
        },
        d: {},
        LT: {
            timeStyle: "short"
        },
        D: {
            day: "numeric"
        }
    };
    let options = {
        ...formatMap[formatToken]
    };
    // We only need to support month/day-only variants for the omitYear option
    if (formatOptions?.omitYear) {
        // Replace preset with explicit field-level control
        if (formatToken === "ll") {
            options = {
                month: "short",
                day: "numeric"
            };
        } else if (formatToken === "LL") {
            options = {
                month: "long",
                day: "numeric"
            };
        } else if (formatToken === "L") {
            options = {
                month: "2-digit",
                day: "2-digit"
            };
        }
    }
    // Check if dateTime is a Temporal.Instant and convert to ZonedDateTime
    if (dateTime instanceof Temporal.Instant) {
        // Get system timezone if not derivable from the input
        const resolvedTimeZone = getSystemTimeZone();
        dateTime = dateTime.toZonedDateTimeISO(resolvedTimeZone);
    }
    // Case when formatToken is "d" (day of week)
    if (formatToken === "d") {
        // Return numeric day of week (0 = Sunday) manually
        return String(dateTime.dayOfWeek % 7); // Temporal is 1=Mon..7=Sun, Moment is 0=Sun..6=Sat
    }
    if (formatToken === "D") {
        // Return numeric day of month
        return String(dateTime.day);
    }
    // Generate the formatter with the specified locale and options using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat(resolvedLocale, {
        timeZone: dateTime.timeZoneId,
        ...options
    });
    // Fallback: check if format supports Temporal.ZonedDateTime directly
    try {
        return formatter.format(dateTime); // supported in modern runtimes
    } catch  {
        // Fallback to JS Date using epochMilliseconds
        return formatter.format(new Date(dateTime.epochMilliseconds));
    }
}
/**
 * Converts a moment-like input (Date or ISO string) into Temporal.ZonedDateTime,
 * simulating the behavior of moment(date).
 * @param input - A Date object or ISO 8601 string
 * @param [timeZone] - Optional. Defaults to system time zone.
 * @returns Temporal.ZonedDateTime
 */ export function fromMomentLike(input, timeZone = getSystemTimeZone()) {
    const instant = Temporal.Instant.from(typeof input === "string" ? input : input.toISOString());
    return instant.toZonedDateTimeISO(timeZone);
}
/**
 * Retrieves the current date with  formatting options
 * @param options options for date formatting
 * @returns The formatted date components
 */ export function getHumanizedDate(options) {
    const { weekdayFormat = "short", dayFormat = "2-digit", monthFormat = "short", toUpperCase = true, timeZone = getSystemTimeZone() } = options || {};
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    const locale = getCurrentLocale();
    let dayOfWeek = now.toLocaleString(locale, {
        weekday: weekdayFormat
    });
    const dateOfMonth = now.toLocaleString(locale, {
        day: dayFormat
    });
    let month = now.toLocaleString(locale, {
        month: monthFormat
    });
    if (toUpperCase) {
        dayOfWeek = dayOfWeek.toUpperCase();
        month = month.toUpperCase();
    }
    return {
        dayOfWeek,
        dateOfMonth,
        month
    };
}
/**
 * Function to get the current hour
 * @param timeZone Optional timezone string. If not provided, uses system timezone.
 */ export function getCurrentHour(timeZone = getSystemTimeZone()) {
    const now = Temporal.Now.zonedDateTimeISO(timeZone);
    return now.hour;
}
/**
 * Get month name from month index (1-12)
 */ export function getTemporalMonthNameFromMonth(month) {
    return new Temporal.PlainDate(2000, month, 1).toLocaleString(getCurrentLocale(), {
        month: "long"
    });
}
/**
 * Get day name from day of week index (1-7)
 */ export function getTemporalDayNameFromDayOfWeek(dayOfWeek) {
    return new Temporal.PlainDate(2000, 1, 2 + dayOfWeek).toLocaleString(getCurrentLocale(), {
        weekday: "long"
    });
}
/**
 * Check if the timestamp is expired based on the expiry duration (Temporal API version)
 * @param timestamp The timestamp to compare (Temporal.Instant or null)
 * @param expiryDuration The duration in seconds to compare with the timestamp
 * @returns isExpired: boolean, delta: number
 */ export const isTimeStampExpired = (timestamp, expiryDuration)=>{
    if (timestamp === null) {
        return {
            isExpired: false,
            delta: 0
        };
    }
    const nowInstant = Temporal.Now.instant();
    const delta = nowInstant.since(timestamp, {
        largestUnit: "seconds"
    }).seconds;
    const isExpired = delta > expiryDuration;
    return {
        isExpired,
        delta
    };
};
/**
 * Get difference in days between two Temporal.PlainDate dates
 * @param endDate - The end date
 * @param startDate - The start date
 * @returns The number of days between the two dates
 */ export function diffInDays(endDate, startDate) {
    return endDate.since(startDate).days;
}
